import {
  ALWAYS,
  type FieldError,
  newId,
  type Status,
  type Transition,
  validateWorkflowDraft,
  type WorkflowDraft,
  type WorkflowIssue,
} from '@servicetim/shared';
import { keyFromName, moveItem, uniqueKey } from '../../projects/lib/list-utils';

export const statusKeys = (draft: WorkflowDraft): Set<string> =>
  new Set(draft.statuses.map((s) => s.key));
export const transitionKeys = (draft: WorkflowDraft): Set<string> =>
  new Set(draft.transitions.map((t) => t.key));

export function newStatus(draft: WorkflowDraft): Status {
  return {
    id: newId(),
    key: uniqueKey('new_status', statusKeys(draft)),
    name: 'New status',
    category: 'todo',
    color: null,
    position: draft.statuses.length,
    portalName: null,
  };
}

export function newTransition(draft: WorkflowDraft): Transition {
  const first = draft.statuses[0]?.id ?? draft.initialStatusId;
  const target = draft.statuses[1]?.id ?? first;
  return {
    id: newId(),
    key: uniqueKey('transition', transitionKeys(draft)),
    name: 'New transition',
    fromStatusIds: [first],
    toStatusId: target,
    conditions: ALWAYS,
    validators: [],
    postFunctions: [],
    formId: null,
    position: draft.transitions.length,
  };
}

const renumber = <T extends { position: number }>(list: readonly T[]): T[] =>
  list.map((item, position) => ({ ...item, position }));

export function addStatus(draft: WorkflowDraft): WorkflowDraft {
  return { ...draft, statuses: renumber([...draft.statuses, newStatus(draft)]) };
}

export function updateStatus(
  draft: WorkflowDraft,
  index: number,
  patch: Partial<Status>,
): WorkflowDraft {
  return {
    ...draft,
    statuses: draft.statuses.map((s, i) => (i === index ? { ...s, ...patch } : s)),
  };
}

/** Removes a status; transitions into it disappear, `from` lists drop it, the initial status moves on. */
export function removeStatus(draft: WorkflowDraft, statusId: string): WorkflowDraft {
  if (draft.statuses.length <= 1) return draft;
  const statuses = renumber(draft.statuses.filter((s) => s.id !== statusId));
  const transitions = renumber(
    draft.transitions
      .filter((t) => t.toStatusId !== statusId)
      .map((t) => ({
        ...t,
        fromStatusIds:
          t.fromStatusIds === null ? null : t.fromStatusIds.filter((id) => id !== statusId),
      })),
  );
  const initialStatusId =
    draft.initialStatusId === statusId
      ? (statuses[0]?.id ?? draft.initialStatusId)
      : draft.initialStatusId;
  return { ...draft, statuses, transitions, initialStatusId };
}

export function moveStatus(draft: WorkflowDraft, from: number, to: number): WorkflowDraft {
  return { ...draft, statuses: renumber(moveItem(draft.statuses, from, to)) };
}

export function addTransition(draft: WorkflowDraft): WorkflowDraft {
  return { ...draft, transitions: renumber([...draft.transitions, newTransition(draft)]) };
}

export function updateTransition(
  draft: WorkflowDraft,
  index: number,
  patch: Partial<Transition>,
): WorkflowDraft {
  return {
    ...draft,
    transitions: draft.transitions.map((t, i) => (i === index ? { ...t, ...patch } : t)),
  };
}

export function removeTransition(draft: WorkflowDraft, index: number): WorkflowDraft {
  return { ...draft, transitions: renumber(draft.transitions.filter((_, i) => i !== index)) };
}

export function moveTransition(draft: WorkflowDraft, from: number, to: number): WorkflowDraft {
  return { ...draft, transitions: renumber(moveItem(draft.transitions, from, to)) };
}

/** Renames a status and, when the key still matches the old name, keeps the key in step. */
export function renameStatus(draft: WorkflowDraft, index: number, name: string): WorkflowDraft {
  const current = draft.statuses[index];
  if (!current) return draft;
  const derived = current.key === keyFromName(current.name) || current.key.startsWith('new_status');
  const taken = new Set([...statusKeys(draft)].filter((k) => k !== current.key));
  return updateStatus(draft, index, {
    name,
    ...(derived ? { key: uniqueKey(keyFromName(name) || current.key, taken) } : {}),
  });
}

export function renameTransition(draft: WorkflowDraft, index: number, name: string): WorkflowDraft {
  const current = draft.transitions[index];
  if (!current) return draft;
  const derived = current.key === keyFromName(current.name) || current.key.startsWith('transition');
  const taken = new Set([...transitionKeys(draft)].filter((k) => k !== current.key));
  return updateTransition(draft, index, {
    name,
    ...(derived ? { key: uniqueKey(keyFromName(name) || current.key, taken) } : {}),
  });
}

export const draftEquals = (a: WorkflowDraft, b: WorkflowDraft): boolean =>
  JSON.stringify(a) === JSON.stringify(b);

export const statusNames = (draft: WorkflowDraft): Record<string, string> =>
  Object.fromEntries(draft.statuses.map((s) => [s.id, s.name]));

/** Mirrors the API's `draftIssuesToErrors`: structural issues as JSON paths of the draft body. */
export function draftIssuesToErrors(draft: WorkflowDraft, issues: WorkflowIssue[]): FieldError[] {
  return issues.map((issue) => {
    const si = draft.statuses.findIndex((s) => s.id === issue.ref);
    if (si >= 0) return { path: `statuses.${si}`, message: issue.message };
    const ti = draft.transitions.findIndex(
      (t) =>
        t.id === issue.ref ||
        t.toStatusId === issue.ref ||
        t.fromStatusIds?.includes(issue.ref ?? ''),
    );
    if (ti >= 0) return { path: `transitions.${ti}`, message: issue.message };
    if (issue.code === 'initial_status_missing')
      return { path: 'initialStatusId', message: issue.message };
    return { path: 'statuses', message: issue.message };
  });
}

/** Client-side structural validation (same rules as the API's `validateWorkflowDraft`). */
export function validateDraft(draft: WorkflowDraft): FieldError[] {
  return draftIssuesToErrors(draft, validateWorkflowDraft(draft));
}

/** Summary of a transition's rules for the table ("2 conditions · 1 validator"). */
export function ruleSummary(t: Transition): string {
  const conditions = countConditions(t.conditions);
  const parts = [
    conditions > 0 ? `${conditions} condition${conditions === 1 ? '' : 's'}` : null,
    t.validators.length > 0
      ? `${t.validators.length} validator${t.validators.length === 1 ? '' : 's'}`
      : null,
    t.postFunctions.length > 0
      ? `${t.postFunctions.length} post-function${t.postFunctions.length === 1 ? '' : 's'}`
      : null,
  ].filter((p): p is string => p !== null);
  return parts.length > 0 ? parts.join(' · ') : 'No rules';
}

function countConditions(rule: Transition['conditions']): number {
  if ('all' in rule) return rule.all.reduce((n, r) => n + countConditions(r), 0);
  if ('any' in rule) return rule.any.reduce((n, r) => n + countConditions(r), 0);
  if ('not' in rule) return countConditions(rule.not);
  return 1;
}
